import {
  buildDailySettlementDataFromListings,
  getSettlementGross,
  isCompleteOrder,
} from '../DailySettlementWrapper/dailySettlementData'
import { getFacilityFee } from '../DailySettlementWrapper/getFacilityFee'
import {
  EMPTY_DISPLAY_VALUE,
  getEventNameForOrder,
} from '../../utilities/helpers'

export function normalizePaymentMethodKey(paymentMethod) {
  return String(paymentMethod ?? '')
    .trim()
    .toLowerCase()
}

/**
 * Group complete settlement orders by event / package display name.
 * @param {Array<Record<string, unknown>>} raw — filtered rows from buildDailySettlementDataFromListings
 * @returns {Array<{ name: string, orderCount: number, ticketsSold: number, grossSales: number, facilityFees: number }>}
 */
export function buildByProductOrEvent(raw) {
  const byProductOrEvent = (raw || []).reduce((acc, order) => {
    if (!isCompleteOrder(order)) return acc

    const displayName = getEventNameForOrder(order)
    const name =
      !displayName || displayName === EMPTY_DISPLAY_VALUE
        ? 'Unknown'
        : displayName

    if (!acc[name]) {
      acc[name] = {
        name,
        orderCount: 0,
        ticketsSold: 0,
        grossSales: 0,
        facilityFees: 0,
      }
    }

    acc[name].orderCount += 1
    acc[name].ticketsSold += Number(order?.quantity ?? 1)
    acc[name].grossSales += getSettlementGross(order)
    acc[name].facilityFees += getFacilityFee(order)
    return acc
  }, {})

  return Object.values(byProductOrEvent).sort((a, b) =>
    a.name.localeCompare(b.name)
  )
}

/**
 * @param {Array<Record<string, unknown>>} apiRows — rows from getPaymentListingsV4
 * @param {string[]} [paymentMethods] — lowercase payment method keys; empty = all
 */
export function buildSummaryByPaymentMethodData(apiRows, paymentMethods = []) {
  const { raw, byPaymentMethod } = buildDailySettlementDataFromListings(apiRows)
  const selected = (paymentMethods || [])
    .map(normalizePaymentMethodKey)
    .filter(Boolean)
  const selectedSet = new Set(selected)
  const hasFilter = selectedSet.size > 0

  const filteredByPaymentMethod = hasFilter
    ? byPaymentMethod.filter((row) =>
        selectedSet.has(normalizePaymentMethodKey(row.paymentMethod))
      )
    : byPaymentMethod

  const filteredRaw = hasFilter
    ? raw.filter((order) =>
        selectedSet.has(normalizePaymentMethodKey(order?.paymentMethod))
      )
    : raw

  return {
    raw,
    byPaymentMethod: filteredByPaymentMethod,
    byProductOrEvent: buildByProductOrEvent(filteredRaw),
    availablePaymentMethods: byPaymentMethod
      .map((row) => ({
        value: normalizePaymentMethodKey(row.paymentMethod),
        label: row.paymentMethod,
      }))
      .filter((opt) => opt.value)
      .sort((a, b) => a.label.localeCompare(b.label)),
  }
}

export function sumMetricRows(rows) {
  if (!rows?.length) return null
  return rows.reduce(
    (acc, row) => ({
      orderCount: acc.orderCount + (row.orderCount ?? 0),
      ticketsSold: acc.ticketsSold + (row.ticketsSold ?? 0),
      grossSales: acc.grossSales + Number(row.grossSales ?? 0),
      facilityFees: acc.facilityFees + Number(row.facilityFees ?? 0),
    }),
    {
      orderCount: 0,
      ticketsSold: 0,
      grossSales: 0,
      facilityFees: 0,
    }
  )
}
